import React, { useContext, useEffect, useRef, useState } from "react";
import GlobalConfig from "../context/GlobalConfig";
import Footer from "../components/Footer";
import Head from "./Head";
import { languageData } from "../constants/languageData";
import JourneyConfig from "../context/JourneyConfig";
import RenderSvg from "../components/RenderSvg";
import Loader from "../components/Loader/Loader";
import { trackAction } from "../utils/hyperdx";

function VerificationTerminated({ stepperFunc,steps }) {
  const { config } = useContext(GlobalConfig);
  const { journey } = useContext(JourneyConfig);
  
  // Helper function to validate and parse redirectTime as a number
  const validateRedirectTime = (value) => {
    if (value == null || value === "") return null;
    const parsed = Number.parseInt(value, 10);
    return (Number.isNaN(parsed) || parsed < 0) ? null : parsed;
  };

  // Get values: check journey.processingConfig first, then fallback to sessionStorage
  const failureRedirectUrl = journey?.processingConfig?.failureRedirectUrl ?? sessionStorage.getItem("failureRedirectUrl");
  const storedStatusOption = config?.userSettings?.showStatusScreenBeforeRedirection ?? sessionStorage.getItem("showStatusScreenBeforeRedirection");
  const redirectTimeValue = journey?.processingConfig?.redirectTime ?? sessionStorage.getItem("redirectTime");
  const language = sessionStorage.getItem("language") ?? "en";
  // Validate redirectTime is a valid number
  const redirectTime = validateRedirectTime(redirectTimeValue);

  console.log("journey?.processingConfig",journey?.processingConfig);
  console.log("showStatusScreenBeforeRedirection",config?.userSettings?.showStatusScreenBeforeRedirection)


  // State management
  const [showStatusScreenBeforeRedirection, setShowStatusScreenBeforeRedirection] = useState(
    storedStatusOption != null 
      ? (typeof storedStatusOption === "boolean" ? storedStatusOption : storedStatusOption === "true")
      : config?.userSettings?.showStatusScreenBeforeRedirection ?? true
  );
  const [loading, setLoading] = useState(true);
  const [count, setCount] = useState(-1);
  const hasMarkedComplete = useRef(false);
  
  // Constants
  const defaultRedirectTime = 1;
  const shouldRedirect = !!failureRedirectUrl;
  const shouldShowUI = !shouldRedirect || showStatusScreenBeforeRedirection;
  const redirectText = languageData[language]?.redirect ?? languageData["en"]?.redirect;

  // Terminal outcome: journey terminated (e.g. retries exceeded).
  useEffect(() => {
    trackAction("journey:outcome", {
      outcome: "terminated",
      reason: sessionStorage.getItem("errorReason") || undefined,
    });
  }, []);

  useEffect(() => {
    const handleFailure = async () => {
      if (!hasMarkedComplete.current) {
        hasMarkedComplete.current = true;
        window.parent.postMessage("Retries Exceeded", "*");
        
        setLoading(!shouldShowUI);
        
        if (shouldRedirect) {
          const initialCount = redirectTime != null ? redirectTime : defaultRedirectTime;
          setCount(initialCount);
        }
      }
    };

    handleFailure();
  }, [shouldRedirect, shouldShowUI, redirectTime]);

  // Handle countdown and redirection
  useEffect(() => {
    if (count < 0) return;
    
    // Safety check: if redirect URL is invalid, don't proceed with countdown
    if (shouldRedirect && !failureRedirectUrl) {
      setCount(-1);
      return;
    }
    
    const timer = setTimeout(() => {
      if (count > 0) {
        setCount(count - 1);
      } else if (shouldRedirect && failureRedirectUrl) {
        clearSessionStorage();
        window.location.href = failureRedirectUrl;
      }
    }, 1000);
    
    return () => clearTimeout(timer);
  }, [count, shouldRedirect, failureRedirectUrl]);

  const clearSessionStorage = () => {
    const itemsToRemove = [
      "sessionId", "jId", "currentStep", "redirectTime", "companyLogo",
      "document", "ipAddress", "selectedCountry", "failureRedirectUrl",
      "successRedirectUrl", "documentFailureReason", "showStatusScreenBeforeRedirection",
      "terminationMessage"
    ];
    itemsToRemove.forEach(item => sessionStorage.removeItem(item));
  };

  const defaultLanguageData = {
    errorHandler: {
      heading: "Verification Terminated",
      subHeading1: "You’ve reached the maximum number of attempts. This may be due to invalid IDs or poor image quality.",
      subHeading2: "",
    },
  };

  const errorHandlerTexts = defaultLanguageData.errorHandler;
  // Backend-provided terminal message (e.g. dedup TERMINATE mode). When present it overrides the
  // default (retries) copy so the page reflects the actual reason the session was stopped.
  const backendTerminationMessage = sessionStorage.getItem("terminationMessage");

  if (loading || !shouldShowUI) {
    return <Loader />;
  }
  return (
    <>
      <Head 
        style={{ zIndex: "100" }} 
        showLanguages={false} 
        showHelp={false}
        showMobileFlow={false}
      />
      <div className="parent-container">
        <div className="main">
          <div className="left-section">
            <RenderSvg
              config={config}
              id={"analusing"}
              svgType={"error"}
              className="w-[180px] h-[180px] md:w-[200px] md:h-[200px] md:relative fixed"
              style={{ marginTop: "3rem" }}
            />
          </div>
          <div
            className="right-section md:items-start items-center"
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              gap: 24,
              fontFamily: "Inter, sans-serif",
              
            }}
          >
            <div
              className="flex flex-col"
              style={{ gap: "16px", marginBottom: "auto" }}
            >
              <div className="text-[18px] md:text-[28px] font-semibold text-[#000000] text-center md:text-left">
                {errorHandlerTexts.heading}
              </div>
              <div className="text-[12px] md:text-[16px] font-semibold text-[#656565] text-center md:text-left">
                {backendTerminationMessage || errorHandlerTexts.subHeading1}
              </div>
              {failureRedirectUrl && (
                <div className="text-[12px] md:text-[16px] font-semibold text-[#656565] text-center md:text-left">
                  {count > 0 ? (
                    <>
                      {redirectText} {count}...{" "}
                      {languageData[language]?.redirectCta?.or ??
                        languageData["en"]?.redirectCta?.or}{" "}
                      <span
                        onClick={() => {
                          clearSessionStorage();
                          window.location.href = failureRedirectUrl;
                        }}
                        style={{
                          color: "#1658aa",
                          cursor: "pointer",
                        }}
                      >
                        {languageData[language]?.redirectCta?.clickHere ??
                          languageData["en"]?.redirectCta?.clickHere}
                      </span>
                    </>
                  ) : (
                    <>
                      <span
                        onClick={() => {
                          clearSessionStorage();
                          window.location.href = failureRedirectUrl;
                        }}
                        style={{
                          color: "#1658aa",
                          cursor: "pointer",
                        }}
                      >
                        {languageData[language]?.redirectCta?.clickHereStart ??
                          languageData["en"]?.redirectCta?.clickHereStart}
                      </span>
                      {" "}
                      {languageData[language]?.redirectCta?.toContinue ??
                        languageData["en"]?.redirectCta?.toContinue}
                    </>
                  )}
                </div>
              )}
            </div>
            <div className="w-full">
              <Footer />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default VerificationTerminated;
